import L from "leaflet";

/**
 * Ray-casting point-in-polygon (lng/lat rings as [lat, lng] or LatLng).
 * @param {{lat:number,lng:number}} point
 * @param {Array<{lat:number,lng:number}|[number,number]>} ring
 */
export function pointInPolygon(point, ring) {
  if (!point || !ring || ring.length < 3) return false;
  const x = Number(point.lng);
  const y = Number(point.lat);
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const pi = ring[i];
    const pj = ring[j];
    const xi = Number(Array.isArray(pi) ? pi[1] : pi.lng);
    const yi = Number(Array.isArray(pi) ? pi[0] : pi.lat);
    const xj = Number(Array.isArray(pj) ? pj[1] : pj.lng);
    const yj = Number(Array.isArray(pj) ? pj[0] : pj.lat);
    const intersect =
      yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi + 0.0) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

/**
 * Extract outer rings from GeoJSON as arrays of {lat,lng}.
 */
export function ringsFromGeojson(gj) {
  if (!gj) return [];
  const rings = [];
  const pushPolygon = (coords) => {
    if (!coords || !coords[0]) return;
    const outer = coords[0].map((c) => ({ lat: c[1], lng: c[0] }));
    if (outer.length >= 3) rings.push(outer);
  };
  const type = gj.type;
  if (type === "Feature") {
    return ringsFromGeojson(gj.geometry);
  }
  if (type === "FeatureCollection") {
    (gj.features || []).forEach((f) => rings.push(...ringsFromGeojson(f)));
    return rings;
  }
  if (type === "Polygon") {
    pushPolygon(gj.coordinates);
  } else if (type === "MultiPolygon") {
    (gj.coordinates || []).forEach(pushPolygon);
  }
  return rings;
}

/**
 * Sample latlngs from a LineString / MultiLineString geojson.
 * @returns {Array<{lat:number,lng:number}>}
 */
export function sampleRouteLatLngs(gj, maxPoints = 400) {
  if (!gj) return [];
  const pts = [];
  const pushLine = (coords) => {
    (coords || []).forEach((c) => {
      if (c && c.length >= 2) pts.push({ lat: c[1], lng: c[0] });
    });
  };
  const walk = (g) => {
    if (!g) return;
    if (g.type === "Feature") return walk(g.geometry);
    if (g.type === "FeatureCollection") {
      (g.features || []).forEach(walk);
      return;
    }
    if (g.type === "LineString") pushLine(g.coordinates);
    else if (g.type === "MultiLineString") (g.coordinates || []).forEach(pushLine);
  };
  walk(gj);
  if (pts.length <= maxPoints) return pts;
  const step = Math.ceil(pts.length / maxPoints);
  return pts.filter((_, i) => i % step === 0);
}

/**
 * Whether fence geometry covers / intersects any of the route samples.
 * @returns {{ ok: boolean, reason: 'no_geometry'|'no_intersection'|'ok' }}
 */
export function cercaIntersectsRotas(cerca, routeGeojsons) {
  if (!cerca) return { ok: false, reason: "no_geometry" };

  const samples = [];
  (routeGeojsons || []).forEach((gj) => {
    samples.push(...sampleRouteLatLngs(gj));
  });
  if (!samples.length) return { ok: false, reason: "no_intersection" };

  const isCircular = Number(cerca.is_circular) === 1;
  if (isCircular) {
    const lat = Number(cerca.lat_centro);
    const lng = Number(cerca.lng_centro);
    const r = Number(cerca.raio_mts);
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || !Number.isFinite(r) || r <= 0) {
      return { ok: false, reason: "no_geometry" };
    }
    const center = L.latLng(lat, lng);
    for (let i = 0; i < samples.length; i++) {
      if (center.distanceTo(samples[i]) <= r) {
        return { ok: true, reason: "ok" };
      }
    }
    return { ok: false, reason: "no_intersection" };
  }

  let gj = cerca.geojson;
  if (typeof gj === "string") {
    try {
      gj = JSON.parse(gj);
    } catch (e) {
      gj = null;
    }
  }
  const rings = ringsFromGeojson(gj);
  if (!rings.length) {
    // fallback: treat center + tiny radius if only center exists
    const lat = Number(cerca.lat_centro);
    const lng = Number(cerca.lng_centro);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      const center = L.latLng(lat, lng);
      for (let i = 0; i < samples.length; i++) {
        if (center.distanceTo(samples[i]) <= 30) {
          return { ok: true, reason: "ok" };
        }
      }
    }
    return { ok: false, reason: "no_geometry" };
  }

  for (let i = 0; i < samples.length; i++) {
    for (let r = 0; r < rings.length; r++) {
      if (pointInPolygon(samples[i], rings[r])) {
        return { ok: true, reason: "ok" };
      }
    }
  }
  return { ok: false, reason: "no_intersection" };
}
